import { BillingSeatType, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useCombinedQueriesTeamGeneralInfo, useWorkspaceId } from '@heidi/workspace/data';

import { useQueryTeamUpgradeIntent } from '../../data/query/useQueryTeamUpgradeIntent';
import { useQueryUserBillingInfo } from '../../data/query/useQueryUserBillingInfo';
import { useQueryWorkspaceMemberSeats } from '../../data/query/useQueryWorkspaceMemberSeats';

export type TeamMaxUpgradeState = {
  /** A Practice team admin with team upgrades on: the team, not their own seat, decides what is left to sell. */
  isTeamAdminUpgrade: boolean;
  /**
   * Some member still holds a Standard seat with no Max upgrade scheduled.
   * `undefined` while unknown (not a team-admin upgrade, or still loading).
   */
  hasMembersLeftToUpgrade: boolean | undefined;
};

/**
 * What a Practice admin can still upgrade: members on Standard seats that the
 * saved team upgrade does not already schedule (or hold) on Max. Both reads are
 * admin-only, so nobody else asks.
 */
export function useTeamMaxUpgradeState({
  isTeamUpgradesEnabled,
}: {
  /** `release-heidi-2-team-upgrades` (app-resolved, PostHog). */
  isTeamUpgradesEnabled: boolean;
}): TeamMaxUpgradeState {
  const { data: billingInfo } = useQueryUserBillingInfo();
  const { isTeamAdmin } = useCombinedQueriesTeamGeneralInfo();
  const workspaceId = useWorkspaceId();

  const isTeamAdminUpgrade =
    isTeamUpgradesEnabled &&
    billingInfo?.new_product_tier === SubscriptionTier.CLINICIAN_PRO_PLUS &&
    isTeamAdmin === true;
  const seatsQuery = useQueryWorkspaceMemberSeats({
    workspaceId,
    enabled: isTeamAdminUpgrade,
  });
  const intentQuery = useQueryTeamUpgradeIntent({ enabled: isTeamAdminUpgrade });

  if (!isTeamAdminUpgrade || !seatsQuery.isSuccess || !intentQuery.isSuccess) {
    return { isTeamAdminUpgrade, hasMembersLeftToUpgrade: undefined };
  }

  const onMax = new Set(intentQuery.data?.users.map((user) => user.userId) ?? []);
  const hasMembersLeftToUpgrade = [...seatsQuery.data].some(
    ([userRefId, seatType]) =>
      seatType === BillingSeatType.STANDARD && !onMax.has(userRefId)
  );
  return { isTeamAdminUpgrade, hasMembersLeftToUpgrade };
}
